import dayjs from 'dayjs';
import coSyncConfig, {
  CoSyncInfo,
  CoSyncTaskConfig,
  getName,
} from '@/workerProcessRenderer/scripts/CoSyncEngine/common/CoSyncConfig';
import { SyncTaskUIStatus } from '@/renderer/scripts/CoSync/SyncCenter/SyncCenter';
import {
  CoSyncRunningSubStatus,
  CoSyncStatus,
  CoSynStatusInfo,
  SyncStatusRunningInfo,
} from '@/workerProcessRenderer/scripts/CoSyncEngine/common/CoSyncEvent';
import { ReportEventName } from '@/common/reporter/eventDef';

export class CoSyncReporter {
  private static instance: CoSyncReporter | null = null;

  public static getInstance(): CoSyncReporter {
    if (CoSyncReporter.instance) return CoSyncReporter.instance;
    CoSyncReporter.instance = new CoSyncReporter();
    return CoSyncReporter.instance;
  }

  private constructor() {
    //
  }

  private createSeq = 0;
  private createTimeStampInMs = 0;

  /**
   * 上报开始创建同步盘
   *
   * @param {CoSyncTaskConfig} taskConfig
   * @memberof CoSyncReporter
   */
  public reportCreateStart(taskConfig: CoSyncTaskConfig) {
    logger.ui.info('reportCreateStart', taskConfig);
    this.createSeq = Math.random();
    this.createTimeStampInMs = dayjs().valueOf();
    reporter.report(ReportEventName.CoSyncCreateBegin, {
      seq: this.createSeq,
      startWay: taskConfig.syncPath.isCreateBaseLocal ? 'from_local' : 'from_cloud',
      strategy: taskConfig.syncSetting.strategy,
      timingType: taskConfig.syncSetting.timingType,
      conflictStrategy: taskConfig.syncSetting.conflictStrategy,
      localPath: taskConfig.syncPath.localPath,
      remotePath: taskConfig.syncPath.remotePath,
      name: getName(taskConfig.syncPath),
    });
  }

  /**
   * 上报同步盘创建成功/失败
   *
   * @param {('success' | 'failed')} result
   * @param {string} error
   * @memberof CoSyncReporter
   */
  reportCreateFinish(
    result: 'success' | 'failed',
    detail: {
      error?: string;
      coSyncInfo?: CoSyncInfo;
    },
  ) {
    logger.ui.info('reportCreateFinish', result, detail);
    let coSyncInfoReportData: ReturnType<typeof CoSyncReporter.buildCoSyncInfoReportData> | null = null;
    if (detail.coSyncInfo) {
      coSyncInfoReportData = CoSyncReporter.buildCoSyncInfoReportData(detail.coSyncInfo);
    }

    reporter.report(ReportEventName.CoSyncCreateFinish, {
      seq: this.createSeq,
      result,
      error: detail.error ? detail.error : 'success',
      durationInMs: this.createTimeStampInMs === 0 ? -1 : dayjs().valueOf() - this.createTimeStampInMs,

      name: coSyncInfoReportData?.name ?? 'null',
      localSyncId: detail.coSyncInfo?.localSyncId ?? 'null',
      syncId: detail.coSyncInfo?.syncId ?? 'null',
      localPath: coSyncInfoReportData?.localPath ?? 'null',
      remotePath: coSyncInfoReportData?.remotePath ?? 'null',
      spaceId: coSyncInfoReportData?.spaceId ?? 'null',
      spaceTag: coSyncInfoReportData?.spaceTag ?? 'null',
      spaceOrgId: coSyncInfoReportData?.spaceOrgId ?? 'null',
      isCreateBaseLocal: coSyncInfoReportData?.isCreateBaseLocal ?? 'null',
      conflictStrategy: coSyncInfoReportData?.conflictStrategy ?? 'null',
      strategy: coSyncInfoReportData?.strategy ?? 'null',
      timingType: coSyncInfoReportData?.timingType ?? 'null',
    });
    this.createSeq = 0;
    this.createTimeStampInMs = 0;
  }

  /**
   * 上报配置弹窗展示
   *
   * @param {ShowConfigDialogSource} showFrom
   * @memberof CoSyncReporter
   */
  public reportShowConfigDialog(showFrom: ShowConfigDialogSource) {
    logger.ui.info('reportShowConfigDialog', showFrom);
    reporter.report(ReportEventName.CoSyncConfigDialogShow, {
      showFrom,
    });
  }

  /**
   * 上报配置弹窗关闭
   *
   * @param {('confirm' | 'cancel' | 'close')} action
   * @memberof CoSyncReporter
   */
  public reportConfigDialogDone(action: 'confirm' | 'cancel' | 'close', isEditable: boolean) {
    logger.ui.info('reportConfigDialogDone', action, isEditable);
    reporter.report(ReportEventName.CoSyncConfigDialogDone, {
      action,
      isEditable: isEditable ? 1 : 0,
    });
  }

  private syncStartBeginTimeStampInMs = 0;
  private syncStopBeginTimeStampInMs = 0;
  private syncDeleteBeginTimeStampInMs = 0;
  public reportSyncCmdBegin(cmdType: 'start' | 'stop' | 'delete', coSyncInfo: CoSyncInfo) {
    logger.ui.info('reportSyncCmdBegin', cmdType, coSyncInfo);
    const coSyncInfoReportData = CoSyncReporter.buildCoSyncInfoReportData(coSyncInfo);
    switch (cmdType) {
      case 'start':
        {
          this.syncStartBeginTimeStampInMs = dayjs().valueOf();
          reporter.report(ReportEventName.CoSyncStartBegin, { ...coSyncInfoReportData });
        }
        break;

      case 'stop':
        {
          this.syncStopBeginTimeStampInMs = dayjs().valueOf();
          reporter.report(ReportEventName.CoSyncStopBegin, { ...coSyncInfoReportData });
        }
        break;

      case 'delete':
        {
          this.syncDeleteBeginTimeStampInMs = dayjs().valueOf();
          reporter.report(ReportEventName.CoSyncDeleteBegin, { ...coSyncInfoReportData });
        }
        break;
    }
  }

  public reportSyncCmdEnd(
    cmdType: 'start' | 'stop' | 'delete',
    coSyncInfo: CoSyncInfo,
    result: 'success' | 'failed',
    error?: string,
  ) {
    logger.ui.info('reportSyncCmdEnd', cmdType, coSyncInfo, result, error);
    const coSyncInfoReportData = CoSyncReporter.buildCoSyncInfoReportData(coSyncInfo);
    switch (cmdType) {
      case 'start':
        {
          const duration = dayjs().valueOf() - this.syncStartBeginTimeStampInMs;
          this.syncStartBeginTimeStampInMs = 0;
          reporter.report(ReportEventName.CoSyncStartEnd, {
            result,
            error: error ? error : 'success',
            duration,
            ...coSyncInfoReportData,
          });
        }
        break;

      case 'stop':
        {
          const duration = dayjs().valueOf() - this.syncStopBeginTimeStampInMs;
          this.syncStopBeginTimeStampInMs = 0;
          reporter.report(ReportEventName.CoSyncStopEnd, {
            result,
            error: error ? error : 'success',
            duration,
            ...coSyncInfoReportData,
          });
        }
        break;

      case 'delete':
        {
          const duration = dayjs().valueOf() - this.syncDeleteBeginTimeStampInMs;
          this.syncDeleteBeginTimeStampInMs = 0;
          reporter.report(ReportEventName.CoSyncDeleteEnd, {
            result,
            error: error ? error : 'success',
            duration,
            ...coSyncInfoReportData,
          });
        }
        break;
    }
  }

  private syncPageShowBeginTimeStampInMs = 0;
  public reportCoSyncPageShowBegin() {
    logger.ui.info('reportCoSyncPageShowBegin');
    this.syncStartBeginTimeStampInMs = dayjs().valueOf();
    reporter.report(ReportEventName.CoSyncPageShowBegin, {});
  }

  public reportCoSyncPageShowEnd(syncTaskCount: number, isWithLoading: boolean) {
    logger.ui.info('reportCoSyncPageShowEnd', syncTaskCount, isWithLoading);
    if (this.syncPageShowBeginTimeStampInMs !== 0) {
      const duration = dayjs().valueOf() - this.syncPageShowBeginTimeStampInMs;
      reporter.report(ReportEventName.CoSyncPageShowEnd, {
        syncTaskCount,
        duration,
        isWithLoading,
      });
      this.syncPageShowBeginTimeStampInMs = 0;
    }
  }

  public reportClickInCoSyncPage(area: ClickAreaInCoSyncPage) {
    logger.ui.info('reportClickInCoSyncPage', area);
    reporter.report(ReportEventName.CoSyncPageClick, {
      area,
    });
  }

  public reportClickBtnInCoSyncPageListItem(
    action: 'start' | 'stop' | 'delete' | 'setting' | 'browseLocal' | 'openSyncList',
  ) {
    logger.ui.info('reportClickBtnInCoSyncPageListItem', action);
    let area: ClickAreaInCoSyncPage | null = null;
    switch (action) {
      case 'start':
        area = ClickAreaInCoSyncPage.StartButtonInItem;
        break;
      case 'stop':
        area = ClickAreaInCoSyncPage.StopButtonInItem;
        break;
      case 'delete':
        area = ClickAreaInCoSyncPage.DeleteButtonInItem;
        break;
      case 'setting':
        area = ClickAreaInCoSyncPage.SettingButtonInItem;
        break;
      case 'browseLocal':
        area = ClickAreaInCoSyncPage.BrowseLocalButtonInItem;
        break;
      case 'openSyncList':
        area = ClickAreaInCoSyncPage.SyncListButtonInItem;
        break;
    }

    this.reportClickInCoSyncPage(area);
  }

  public reportClickBtnInCoSyncPageListHeader(
    action: 'start' | 'stop' | 'delete' | 'setting' | 'createSyncLocal' | 'createSyncremote',
  ) {
    logger.ui.info('reportClickBtnInCoSyncPageListHeader', action);
    let area: ClickAreaInCoSyncPage | null = null;
    switch (action) {
      case 'start':
        area = ClickAreaInCoSyncPage.StartButtonInHeader;
        break;
      case 'stop':
        area = ClickAreaInCoSyncPage.StopButtonInHeader;
        break;
      case 'delete':
        area = ClickAreaInCoSyncPage.DeleteButtonInHeader;
        break;
      case 'setting':
        area = ClickAreaInCoSyncPage.SettingButtonInHeader;
        break;
      case 'createSyncLocal':
        area = ClickAreaInCoSyncPage.CreateLocalButtonInHeader;
        break;
      case 'createSyncremote':
        area = ClickAreaInCoSyncPage.CreateRemoteButtonInHeader;
        break;
    }

    this.reportClickInCoSyncPage(area);
  }

  public reportClickBtnInCoSyncPageRightMenu(
    action: 'start' | 'stop' | 'delete' | 'browseLocal' | 'setting' | 'openSyncList',
  ) {
    logger.ui.info('reportClickBtnInCoSyncPageRightMenu', action);
    let area: ClickAreaInCoSyncPage | null = null;
    switch (action) {
      case 'start':
        area = ClickAreaInCoSyncPage.StartButtonInHeader;
        break;
      case 'stop':
        area = ClickAreaInCoSyncPage.StopButtonInHeader;
        break;
      case 'delete':
        area = ClickAreaInCoSyncPage.DeleteButtonInHeader;
        break;
      case 'setting':
        area = ClickAreaInCoSyncPage.SettingButtonInHeader;
        break;
      case 'browseLocal':
        area = ClickAreaInCoSyncPage.BrowseLocalButtonInMenu;
        break;
      case 'openSyncList':
        area = ClickAreaInCoSyncPage.SyncListButtonInMenu;
        break;
    }

    this.reportClickInCoSyncPage(area);
  }

  /**
   * 上报执行项抽屉开始展示
   *
   * @private
   * @memberof CoSyncReporter
   */
  private operateDrawerShowBeginTimeStampInMs = 0;
  public reportOperateDrawerShowBegin() {
    logger.ui.info('reportOperateDrawerShowBegin');
    this.syncStartBeginTimeStampInMs = dayjs().valueOf();
    reporter.report(ReportEventName.CoSyncOperateDrawerShowBegin, {});
  }

  /**
   * 上报执行项抽屉展示完成
   *
   * @param {number} operateItemCount 执行项目的数量
   * @memberof CoSyncReporter
   */
  public reportOperateDrawerShowEnd(operateItemCount: number) {
    logger.ui.info('reportOperateDrawerShowEnd', operateItemCount);
    if (this.operateDrawerShowBeginTimeStampInMs !== 0) {
      const duration = dayjs().valueOf() - this.operateDrawerShowBeginTimeStampInMs;
      reporter.report(ReportEventName.CoSyncOperateDrawerShowEnd, {
        operateItemCount,
        duration,
      });
    }
  }

  /**
   * 上报执行项抽屉关闭
   *
   * @param {boolean} isStillLoading 是否还在loading
   * @memberof CoSyncReporter
   */
  public reportOperateDrawerClose(isStillLoading: boolean) {
    logger.ui.info('reportOperateDrawerClose', isStillLoading);
    if (this.operateDrawerShowBeginTimeStampInMs !== 0) {
      const duration = dayjs().valueOf() - this.operateDrawerShowBeginTimeStampInMs;
      reporter.report(ReportEventName.CoSyncOperateDrawerClose, {
        isStillLoading: isStillLoading ? 1 : 0,
        duration,
      });

      this.operateDrawerShowBeginTimeStampInMs = 0;
    }
  }

  private historyDrawerShowBeginTimeStampInMs = 0;
  public reportHistoryDrawerShowBegin() {
    logger.ui.info('reportHistoryDrawerShowBegin');
    this.historyDrawerShowBeginTimeStampInMs = dayjs().valueOf();
    reporter.report(ReportEventName.CoSyncHistoryDrawerShowBegin, {});
  }

  public reportHistoryDrawerShowEnd(recordCountOnFirstLoaded: number) {
    logger.ui.info('reportHistoryDrawerShowEnd', recordCountOnFirstLoaded);
    if (this.historyDrawerShowBeginTimeStampInMs !== 0) {
      const duration = dayjs().valueOf() - this.historyDrawerShowBeginTimeStampInMs;
      reporter.report(ReportEventName.CoSyncHistoryDrawerShowEnd, {
        recordCountOnFirstLoaded,
        duration,
      });

      this.historyDrawerShowBeginTimeStampInMs = 0;
    }
  }

  public reportHistoryDrawerClick(area: ClickAreaInHistoryDrawer) {
    logger.ui.info('reportHistoryDrawerClick', area);
    reporter.report(ReportEventName.CoSyncHistoryDrawerClick, {
      area,
    });
  }

  private historyClearBeginTimeStampInMs = 0;
  public reportHistoryBeforeClear(needClear: boolean, count: number) {
    logger.ui.info('reportHistoryBeforeClear', needClear, count);
    this.historyClearBeginTimeStampInMs = dayjs().valueOf();
    reporter.report(ReportEventName.CoSyncHistoryBeforeClear, {
      needClear: needClear ? 1 : 0,
      count,
    });
  }

  public reportHistoryAfterClear(countBefore: number, countAfter: number) {
    logger.ui.info('reportHistoryAfterClear', countBefore, countAfter);
    if (this.historyClearBeginTimeStampInMs !== 0) {
      const duration = dayjs().valueOf() - this.historyClearBeginTimeStampInMs;
      reporter.report(ReportEventName.CoSyncHistoryAfterClear, {
        duration,
        countBefore,
        countAfter,
      });

      this.historyClearBeginTimeStampInMs = 0;
    }
  }

  public reportConflictDrawerShow() {
    logger.ui.info('reportConflictDrawerShow');
    reporter.report(ReportEventName.CoSyncConflictDrawerShow);
  }

  public reportConflictDrawerClick(area: ClickAreaInConflictDrawer) {
    logger.ui.info('reportConflictDrawerClick', area);
    reporter.report(ReportEventName.CoSyncConflictDrawerClick, {
      area,
    });
  }

  public reportConflictNotificationShow() {
    logger.ui.info('reportConflictNotificationShow');
    reporter.report(ReportEventName.CoSyncConflictNotificationShow);
  }

  public reportConflictNotificationClick(area: ClickAreaInConflictNotification) {
    logger.ui.info('reportConflictNotificationClick', area);
    reporter.report(ReportEventName.CoSyncConflictNotificationClick, {
      area,
    });
  }
  /**
   * 上报同步任务的状态变化
   *
   * @param {SyncTaskUIStatus} status
   * @return {*}
   * @memberof CoSyncReporter
   */
  public onTaskStatusChange(statusInfo: CoSynStatusInfo, coSyncInfo: CoSyncInfo) {
    logger.ui.info('onTaskStatusChange', statusInfo, coSyncInfo);
    const reportRunningSubState = (runningInfo: SyncStatusRunningInfo) => {
      switch (runningInfo.subStatus) {
        case CoSyncRunningSubStatus.STATUS_RUNNING_PLANNER_START:
          {
            const coSyncInfoReportData = CoSyncReporter.buildCoSyncInfoReportData(coSyncInfo);
            reporter.report(ReportEventName.CoSyncProgress, {
              status: CoSyncRunningSubStatus.STATUS_RUNNING_PLANNER_START,
              ...coSyncInfoReportData,
            });
          }
          break;

        case CoSyncRunningSubStatus.STATUS_RUNNING_SCANNER_START:
        case CoSyncRunningSubStatus.STATUS_RUNNING_OPERATE_START: {
          break;
        }

        default: {
          const _exhaustiveCheck: never = runningInfo.subStatus;
          return _exhaustiveCheck;
        }
      }
    };

    switch (statusInfo.status) {
      case CoSyncStatus.STATUS_RUNNING: {
        const runningInfo = statusInfo as SyncStatusRunningInfo;
        reportRunningSubState(runningInfo);
        break;
      }

      case CoSyncStatus.STATUS_ERROR:
      case CoSyncStatus.STATUS_WARN:
      case CoSyncStatus.STATUS_CONFLICT:
      case CoSyncStatus.STATUS_SUCCESS: {
        const coSyncInfoReportData = CoSyncReporter.buildCoSyncInfoReportData(coSyncInfo);
        reporter.report(ReportEventName.CoSyncProgress, {
          status: statusInfo.status,
          ...coSyncInfoReportData,
        });
        break;
      }

      case CoSyncStatus.STATUS_IDLE:
      case CoSyncStatus.STATUS_WAIT_FOR_RUNNING:
      case CoSyncStatus.STATUS_RUNNING:
      case CoSyncStatus.STATUS_STOP: {
        // 这些状态不上报，避免上报太频繁
        break;
      }

      default: {
        const _exhaustiveCheck: never = statusInfo.status;
        return _exhaustiveCheck;
      }
    }
  }

  private static buildCoSyncInfoReportData(coSyncInfo: CoSyncInfo) {
    return {
      name: getName(coSyncInfo.config.syncPath),
      localSyncId: coSyncInfo.localSyncId,
      syncId: coSyncInfo.syncId,
      localPath: coSyncInfo.config.syncPath.localPath,
      remotePath: coSyncInfo.config.syncPath.remotePath,
      spaceId: coSyncInfo.config.syncPath.spaceId,
      spaceTag: coSyncInfo?.config.syncPath.spaceTag,
      spaceOrgId: coSyncInfo?.config.syncPath.spaceOrgId,
      isCreateBaseLocal: coSyncInfo?.config.syncPath.isCreateBaseLocal,
      conflictStrategy: coSyncInfo?.config.syncSetting.conflictStrategy,
      strategy: coSyncInfo?.config.syncSetting.strategy,
      timingType: coSyncInfo?.config.syncSetting.timingType,
    };
  }
}

/**
 * 触发配置弹窗的来源
 *
 * @export
 * @enum {number}
 */
export enum ShowConfigDialogSource {
  LocalButtonInEmptyPage = 'LocalButtonInEmptyPage',
  RemoteButtonInEmptyPage = 'RemoteButtonInEmptyPage',
  LocalButtonInListHeader = 'LocalButtonInListHeader',
  RemoteButtonInListHeader = 'RemoteButtonInListHeader',
  SettingButtonInHeader = 'SettingButtonInHeader',
  SettingButtonInItem = 'SettingButtonInItem',
  RemoteButtonInRightClickMenu = 'RemoteButtonInRightClickMenu',
  DropLocalFile = 'DropLocalFile',
}

/**
 * 同步盘页面中的点击区域
 *
 * @export
 * @enum {number}
 */
export enum ClickAreaInCoSyncPage {
  CreateLocalButtonInHeader = 'CreateLocalButtonInListHeader',
  CreateRemoteButtonInHeader = 'CreateRemoteButtonInListHeader',

  CreateLocalButtonInEmptyPage = 'CreateLocalButtonInEmptyPage',
  CreateRemoteButtonInEmptyPage = 'CreateRemoteButtonInEmptyPage',

  StopButtonInHeader = 'StopButtonInHeader',
  StartButtonInHeader = 'StartButtonInHeader',
  SettingButtonInHeader = 'SettingButtonInHeader',
  DeleteButtonInHeader = 'DeleteButtonInHeader',

  StatusAreaInItem = 'StatusAreaInItem',
  StartButtonInItem = 'StartButtonInItem',
  StopButtonInItem = 'StopButtonInItem',
  BrowseLocalButtonInItem = 'BrowseLocalButtonInItem',
  MoreButtonInItem = 'MoreButtonInItem',
  SettingButtonInItem = 'SettingButtonInItem',
  DeleteButtonInItem = 'DeleteButtonInItem',
  SyncListButtonInItem = 'SyncListButtonInItem',

  StartButtonInMenu = 'StartButtonInMenu',
  StopButtonInMenu = 'StopButtonInMenu',
  DeleteButtonInMenu = 'DeleteButtonInMenu',
  BrowseLocalButtonInMenu = 'BrowseLocalButtonInMenu',
  SettingButtonInMenu = 'SettingButtonInMenu',
  SyncListButtonInMenu = 'SyncListButtonInMenu',

  ConflictTips = 'ConflictTips',
}

export enum ClickAreaInHistoryDrawer {
  LoadMore = 'LoadMore',
  Clear = 'Clear',
}

export enum ClickAreaInConflictDrawer {
  OpenLocalFolder = 'OpenLocalFolder',
  OpenRemoteFolder = 'OpenRemoteFolder',
  ConflictResolveKeepBoth = 'ConflictResolveKeepBoth',
  ConflictResolveLocalPrefer = 'ConflictResolveLocalPrefer',
  ConflictResolveRemotePrefer = 'ConflictResolveRemotePrefer',
}

export enum ClickAreaInConflictNotification {
  ShowDetail = 'ShowDetail',
  Ignore = 'Ignore',
}
